import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VideoBackground from './components/VideoBackground';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { UserProvider, useUser } from '@/contexts/UserContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Dashboard from "@/pages/Dashboard";
import ProfessionalNetworking from "@/pages/ProfessionalNetworking";
import { ProfilePage } from "@/pages/ProfilePage";
import { UpdatePasswordPage } from "@/pages/UpdatePasswordPage";
import LoginPage from "@/pages/LoginPage";
import LiveSessionsPage from '@/pages/LiveSessionsPage';
import Achievements from '@/pages/Achievements'
import OnlineCoachingDetailsPage from './pages/OnlineCoachingDetailsPage'
import OnlineCoachingRegisterPage from './pages/OnlineCoachingRegisterPage'
import Skillstree from './pages/Skillstree'
import CategoryDetailPage from './pages/CategoryDetailPage'
import SkillstreeMicroskillPage from './pages/SkillstreeMicroskillPage'
import MicroskillDetailPage from './pages/MicroskillDetailPage'
import PublicProfile from '@/pages/PublicProfile';
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import VerifyOTPPage from "@/pages/VerifyOTPPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SignUpPage from '@/pages/SignUpPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import PlanSelectionPage from '@/pages/PlanSelectionPage';
import FAQPage from '@/pages/FAQPage';
import './App.css';

const queryClient = new QueryClient();

interface RouteConfig {
  path: string;
  component: React.ComponentType;
  showLayout?: boolean;
  requireAuth?: boolean;
}

// Custom 404 Component
const NotFoundPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  useEffect(() => {
    // Show toast notification
    toast.error('Page not found or not available', {
      position: "top-center",
      autoClose: 3000,
    });
    
    // Redirect after showing toast
    const timer = setTimeout(() => {
      if (user) {
        navigate('/dashboard');
      } else {
        // Navigate to root which will handle WordPress redirect
        navigate('/');
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [navigate, user]);
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <h1 className="text-4xl font-bold text-gray-300 mb-4">404</h1>
      <p className="text-lg text-gray-200 mb-8">Page not found or not available</p>
      <p className="text-gray-100">Redirecting you to {user ? 'dashboard' : 'home page'}...</p>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const location = useLocation();
  
  if (!user) {
    // Save the attempted URL for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

// Create route components with proper protection
const createRouteElement = (route: RouteConfig) => {
  const Element = route.component;
  
  if (route.requireAuth) {
    return (
      <ProtectedRoute>
        <Element />
      </ProtectedRoute>
    );
  }
  
  return <Element />;
};

const routes: RouteConfig[] = [
  { path: "/dashboard", component: Dashboard, showLayout: true, requireAuth: true },
  { path: "/professional-networking", component: ProfessionalNetworking, showLayout: true, requireAuth: false },
  { path: "/profile", component: ProfilePage, showLayout: true, requireAuth: true },
  { path: "/online-coaching", component: LiveSessionsPage, showLayout: true, requireAuth: false },
  { path: "/online-coaching/register/:sessionId", component: OnlineCoachingRegisterPage, showLayout: true, requireAuth: false },
  { path: "/online-coaching/join/:meetingId", component: OnlineCoachingDetailsPage, showLayout: true, requireAuth: true },
  { path: "/online-coaching/room/:meetingId", component: OnlineCoachingDetailsPage, showLayout: true, requireAuth: true },
  { path: "/login", component: LoginPage, showLayout: false, requireAuth: false },
  { path: "/forgot-password", component: ForgotPasswordPage, showLayout: false, requireAuth: false },
  { path: "/verify-otp", component: VerifyOTPPage, showLayout: false, requireAuth: false },
  { path: "/reset-password", component: ResetPasswordPage, showLayout: false, requireAuth: false },
  { path: "/signup", component: SignUpPage, showLayout: false, requireAuth: false },
  { path: "/verify-email", component: VerifyEmailPage, showLayout: false, requireAuth: false },
  { path: "/plan-selection", component: PlanSelectionPage, showLayout: false, requireAuth: false },
  { path: "/live-sessions", component: LiveSessionsPage, showLayout: true, requireAuth: true },
  { path: "/achievements", component: Achievements, showLayout: true, requireAuth: true },
  { path: "/update-password", component: UpdatePasswordPage, showLayout: true, requireAuth: true },
  { path: "/skillstree", component: Skillstree, showLayout: true, requireAuth: true },
  { path: "/skillstree/:categoryId", component: CategoryDetailPage, showLayout: true, requireAuth: true },
  { path: "/skillstree/:categoryId/:skillstreeId", component: SkillstreeMicroskillPage, showLayout: true, requireAuth: true },
  { path: "/skillstree/:categoryId/:skillstreeId/:microskillId", component: MicroskillDetailPage, showLayout: true, requireAuth: true },
  { path: "/profile/:username", component: PublicProfile, showLayout: true, requireAuth: true },
  { path: "/profile/:username/:tab", component: PublicProfile, showLayout: true, requireAuth: true },
  { path: "/profile/:username/:tab/:credentialId", component: PublicProfile, showLayout: true, requireAuth: true },
  { path: "/faq", component: FAQPage, showLayout: true, requireAuth: false },
];

const AppContent = () => {
  const location = useLocation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [redirected, setRedirected] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  
  // Handle root path redirect
  useEffect(() => {
    if (location.pathname === '/' && !redirected) {
      setRedirected(true);
      
      if (user) {
        // Authenticated user - redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // Unauthenticated user - redirect to WordPress site
        window.location.href = import.meta.env.VITE_APP_HOME || 'https://www.skillsture.io/master-the-skill-that-matter';
      }
    } else if (location.pathname !== '/') {
      setRedirected(false);
    }
  }, [location.pathname, user, navigate, redirected]);

  // Check if the current path matches any route
  const getCurrentRoute = () => {
    // First check exact matches
    const exactMatch = routes.find(route => {
      if (route.path === location.pathname) return true;
      return false;
    });
    
    if (exactMatch) return exactMatch;
    
    // Check for dynamic route matches
    for (const route of routes) {
      if (route.path.includes(':')) {
        const routeParts = route.path.split('/').filter(Boolean);
        const pathParts = location.pathname.split('/').filter(Boolean);
        
        if (routeParts.length === pathParts.length) {
          const isMatch = routeParts.every((part, index) => {
            return part.startsWith(':') || part === pathParts[index];
          });
          
          if (isMatch) return route;
        }
      }
    }
    
    return null;
  };
  
  const currentRoute = getCurrentRoute();
  const isNotFound = !currentRoute;
  
  // Set not found state after a small delay to prevent flickering
  useEffect(() => {
    if (isNotFound && location.pathname !== '/') {
      const timer = setTimeout(() => {
        setShowNotFound(true);
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setShowNotFound(false);
    }
  }, [isNotFound, location.pathname]);

  // Update body class based on layout
  useEffect(() => {
    if (currentRoute?.showLayout && !isNotFound) {
      document.body.classList.remove('auth-background');
    } else {
      document.body.classList.add('auth-background');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('auth-background');
    };
  }, [currentRoute?.showLayout, location.pathname, isNotFound]);
  
  // Handle not found state
  if (showNotFound) {
    return (
      <>
        <VideoBackground
          webmSrc="/background.webm"
          mp4Src="/background.mp4"
          fallbackImage="/background.png"
        />
        <div className="min-h-screen flex flex-col">
          <div className="sticky top-3 z-50 w-full">
            <Navbar />
          </div>
          <main className="flex-1 w-full max-w-7xl mx-auto">
            <NotFoundPage />
          </main>
          <div className="mt-auto w-full">
            <Footer />
          </div>
        </div>
      </>
    );
  }

  // Don't render anything for root path while redirecting
  if (location.pathname === '/') {
    return null;
  }

  // Render the appropriate layout based on the route
  if (currentRoute?.showLayout) {
    return (
      <>
        <VideoBackground
          webmSrc="/background.webm"
          mp4Src="/background.mp4"
          fallbackImage="/background.png"
        />
        <div className="min-h-screen flex flex-col">
          <div className="sticky top-3 z-50 w-full">
            <Navbar />
          </div>
          <main className="flex-1 w-full max-w-7xl mx-auto">
            <Routes>
              {routes.map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={createRouteElement(route)} 
                />
              ))}
            </Routes>
          </main>
          <div className="mt-auto w-full">
            <Footer />
          </div>
        </div>
      </>
    );
  } else {
    return (
      <div className="min-h-screen flex items-center justify-end">
        <main className="2xl:w-1/3 xl:w-2/5 lg:w-1/2 md:w-3/5 w-full">
          <Routes>
            {routes
              .filter(route => !route.showLayout)
              .map((route) => (
                <Route 
                  key={route.path} 
                  path={route.path} 
                  element={createRouteElement(route)} 
                />
              ))}
          </Routes>
        </main>
      </div>
    );
  }
};

const App = () => (
  <BrowserRouter>
    <UserProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppContent />
          <ToastContainer />
        </TooltipProvider>
      </QueryClientProvider>
    </UserProvider>
  </BrowserRouter>
);

export default App;